import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import {
  RotateCcw, Search, Filter, Download, ChevronUp, ChevronDown,
  Eye, RefreshCw, Loader2, CheckSquare, Square, AlertTriangle, History,
} from 'lucide-react';
import CreditNoteVersionsPanel from '../components/CreditNoteVersionsPanel';
import { useFocusEntity } from '../hooks/useFocusEntity';

const STATUS_BADGE = {
  draft:    'bg-amber-100 text-amber-800 border-amber-300',
  posted:   'bg-emerald-100 text-emerald-800 border-emerald-300',
  reversed: 'bg-slate-200 text-slate-700 border-slate-400',
};
const TYPE_LABELS = {
  full_refund: 'Full Refund',
  partial_refund: 'Partial Refund',
  correction: 'Correction',
  discount_adjustment: 'Discount Adjustment',
};

const formatMoney = (n) => Number(n || 0).toLocaleString('en-US',
  { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const formatDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('en-US',
    { dateStyle: 'short', timeStyle: 'short' }); }
  catch { return iso; }
};

function toCSV(rows) {
  const headers = [
    'Credit Note #', 'Invoice #', 'Type', 'Amount', 'Tax',
    'Total', 'Status', 'Journal #', 'Reason', 'Created At',
  ];
  const escape = (v) => {
    const s = String(v ?? '');
    return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
  };
  const lines = [headers.join(',')];
  rows.forEach((r) => {
    lines.push([
      r.credit_note_number, r.invoice_number || r.invoice_id,
      TYPE_LABELS[r.type] || r.type,
      r.amount, r.tax_amount, r.total_amount, r.status,
      r.linked_journal_no || '', r.reason, r.created_at,
    ].map(escape).join(','));
  });
  return lines.join('\n');
}

const SortHeader = ({ label, k, sortBy, sortDir, onClick }) => (
  <th onClick={() => onClick(k)}
    data-testid={`sort-${k}`}
    className="px-3 py-2 text-left text-xs uppercase tracking-wider opacity-70 cursor-pointer select-none hover:opacity-100">
    <span className="inline-flex items-center gap-1">
      {label}
      {sortBy === k && (sortDir === 'asc' ? <ChevronUp size={11} /> : <ChevronDown size={11} />)}
    </span>
  </th>
);

export default function CreditNotes({ user }) {
  const { focusedId, registerRow } = useFocusEntity();
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');
  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [expandedCnId, setExpandedCnId] = useState(null);

  const isAdmin = (user?.role === 'admin');

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axiosInstance.get('/credit-notes');
      setNotes(r.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load credit notes');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const t = setTimeout(fetchAll, 0);
    return () => clearTimeout(t);
  }, [fetchAll]);

  const filtered = useMemo(() => {
    let rows = notes;
    if (statusFilter !== 'all') rows = rows.filter(r => r.status === statusFilter);
    if (typeFilter !== 'all') rows = rows.filter(r => r.type === typeFilter);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      rows = rows.filter(r =>
        (r.credit_note_number || '').toLowerCase().includes(q) ||
        (r.invoice_number || '').toLowerCase().includes(q) ||
        (r.invoice_id || '').toLowerCase().includes(q) ||
        (r.reason || '').toLowerCase().includes(q)
      );
    }
    const sorted = [...rows].sort((a, b) => {
      const av = a[sortBy], bv = b[sortBy];
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === 'number' && typeof bv === 'number')
        return sortDir === 'asc' ? av - bv : bv - av;
      return sortDir === 'asc'
        ? String(av).localeCompare(String(bv))
        : String(bv).localeCompare(String(av));
    });
    return sorted;
  }, [notes, search, statusFilter, typeFilter, sortBy, sortDir]);

  const totals = useMemo(() => {
    let posted = 0, draft = 0;
    notes.forEach((n) => {
      if (n.status === 'posted') posted += Number(n.total_amount || 0);
      else if (n.status === 'draft') draft += Number(n.total_amount || 0);
    });
    return { posted, draft };
  }, [notes]);

  const toggleSort = (key) => {
    if (sortBy === key) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else { setSortBy(key); setSortDir('desc'); }
  };

  const toggleSelect = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };

  const draftRowsVisible = useMemo(
    () => filtered.filter(r => r.status === 'draft'),
    [filtered],
  );
  const allDraftsSelected = draftRowsVisible.length > 0
    && draftRowsVisible.every(r => selected.has(r.id));

  const toggleSelectAllDrafts = () => {
    if (allDraftsSelected) {
      const next = new Set(selected);
      draftRowsVisible.forEach(r => next.delete(r.id));
      setSelected(next);
    } else {
      const next = new Set(selected);
      draftRowsVisible.forEach(r => next.add(r.id));
      setSelected(next);
    }
  };

  const bulkPost = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Post ${selected.size} draft credit note(s) to accounting? This creates journal entries.`)) return;
    setBusy(true);
    let ok = 0, fail = 0;
    for (const id of selected) {
      const row = notes.find(n => n.id === id);
      if (!row || row.status !== 'draft') continue;
      try {
        await axiosInstance.post(`/credit-notes/${id}/post`);
        ok++;
      } catch { fail++; }
    }
    setBusy(false);
    setSelected(new Set());
    toast.success(`Posted ${ok} · Failed ${fail}`);
    fetchAll();
  };

  const reverseOne = async (cn) => {
    if (!isAdmin) { toast.error('Admin only'); return; }
    const reason = window.prompt(`Reverse ${cn.credit_note_number}? Enter reason (min 3 chars):`);
    if (!reason || reason.trim().length < 3) return;
    try {
      await axiosInstance.post(`/credit-notes/${cn.id}/reverse`,
        null, { params: { reason: reason.trim() } });
      toast.success(`${cn.credit_note_number} reversed`);
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to reverse');
    }
  };

  const exportCSV = () => {
    const csv = toCSV(filtered);
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `credit-notes-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${filtered.length} credit notes to CSV`);
  };

  if (loading) {
    return (
      <div data-testid="cn-loading" className="flex items-center justify-center min-h-[400px]">
        <Loader2 size={32} className="animate-spin opacity-50" />
      </div>
    );
  }

  return (
    <div data-testid="credit-notes-page" className="p-6 space-y-5">
      {/* ── Header ────────────────────────────────────────── */}
      <div className="flex items-start justify-between border-b border-slate-200 pb-4">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <RotateCcw size={26} className="text-amber-600" />
            Credit Notes
          </h1>
          <p className="text-xs text-slate-500 mt-1">
            طبقة التصحيح المحاسبي — Audit-safe correction layer for invoices
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={fetchAll} data-testid="cn-refresh-btn"
            className="px-3 py-1.5 rounded border border-slate-300 hover:bg-slate-50 text-sm flex items-center gap-1">
            <RefreshCw size={14} /> Refresh
          </button>
          <button onClick={exportCSV} data-testid="cn-export-btn"
            disabled={filtered.length === 0}
            className="px-3 py-1.5 rounded border border-emerald-300 bg-emerald-50 hover:bg-emerald-100 text-emerald-800 text-sm flex items-center gap-1 disabled:opacity-40">
            <Download size={14} /> Export CSV
          </button>
        </div>
      </div>

      {/* ── KPIs ──────────────────────────────────────────── */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <div data-testid="cn-kpi-total" className="border border-slate-200 rounded p-3">
          <div className="text-xs uppercase text-slate-500">Total Credit Notes</div>
          <div className="text-2xl font-bold mt-1">{notes.length}</div>
        </div>
        <div data-testid="cn-kpi-posted" className="border border-emerald-200 bg-emerald-50 rounded p-3">
          <div className="text-xs uppercase text-emerald-700">Posted (SAR)</div>
          <div className="text-2xl font-bold mt-1 text-emerald-800">{formatMoney(totals.posted)}</div>
        </div>
        <div data-testid="cn-kpi-draft" className="border border-amber-200 bg-amber-50 rounded p-3">
          <div className="text-xs uppercase text-amber-700">Draft (SAR)</div>
          <div className="text-2xl font-bold mt-1 text-amber-800">{formatMoney(totals.draft)}</div>
        </div>
        <div data-testid="cn-kpi-selected" className="border border-blue-200 bg-blue-50 rounded p-3">
          <div className="text-xs uppercase text-blue-700">Selected</div>
          <div className="text-2xl font-bold mt-1 text-blue-800">{selected.size}</div>
        </div>
      </div>

      {/* ── Toolbar: search + filters + bulk ──────────────── */}
      <div className="flex flex-wrap items-center gap-3 p-3 border border-slate-200 rounded bg-slate-50">
        <div className="flex items-center gap-2 flex-1 min-w-[200px]">
          <Search size={16} className="text-slate-400" />
          <input data-testid="cn-search-input"
            type="text" placeholder="Search CN#, Invoice#, or reason…"
            value={search} onChange={(ev) => setSearch(ev.target.value)}
            className="flex-1 px-2 py-1.5 rounded border border-slate-300 text-sm" />
        </div>
        <div className="flex items-center gap-2">
          <Filter size={14} className="text-slate-500" />
          <select data-testid="cn-status-filter"
            value={statusFilter} onChange={(ev) => setStatusFilter(ev.target.value)}
            className="px-2 py-1.5 rounded border border-slate-300 text-sm">
            <option value="all">All Statuses</option>
            <option value="draft">Draft</option>
            <option value="posted">Posted</option>
            <option value="reversed">Reversed</option>
          </select>
          <select data-testid="cn-type-filter"
            value={typeFilter} onChange={(ev) => setTypeFilter(ev.target.value)}
            className="px-2 py-1.5 rounded border border-slate-300 text-sm">
            <option value="all">All Types</option>
            {Object.entries(TYPE_LABELS).map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </select>
        </div>
        {selected.size > 0 && (
          <button onClick={bulkPost} disabled={busy}
            data-testid="cn-bulk-post"
            className="px-3 py-1.5 rounded bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold flex items-center gap-1 disabled:opacity-50">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <CheckSquare size={14} />}
            Post {selected.size} draft{selected.size > 1 ? 's' : ''}
          </button>
        )}
      </div>

      {/* ── Table ─────────────────────────────────────────── */}
      <div className="border border-slate-200 rounded overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-100 border-b border-slate-200">
            <tr>
              <th className="px-3 py-2 w-8">
                {draftRowsVisible.length > 0 && (
                  <button onClick={toggleSelectAllDrafts}
                    data-testid="cn-select-all-drafts"
                    title="Select all draft rows on this view">
                    {allDraftsSelected
                      ? <CheckSquare size={14} className="text-blue-600" />
                      : <Square size={14} className="text-slate-400" />}
                  </button>
                )}
              </th>
              <SortHeader label="CN #" k="credit_note_number" sortBy={sortBy} sortDir={sortDir} onClick={toggleSort} />
              <SortHeader label="Invoice #" k="invoice_number" sortBy={sortBy} sortDir={sortDir} onClick={toggleSort} />
              <SortHeader label="Type" k="type" sortBy={sortBy} sortDir={sortDir} onClick={toggleSort} />
              <SortHeader label="Amount" k="amount" sortBy={sortBy} sortDir={sortDir} onClick={toggleSort} />
              <SortHeader label="Tax" k="tax_amount" sortBy={sortBy} sortDir={sortDir} onClick={toggleSort} />
              <SortHeader label="Total" k="total_amount" sortBy={sortBy} sortDir={sortDir} onClick={toggleSort} />
              <SortHeader label="Status" k="status" sortBy={sortBy} sortDir={sortDir} onClick={toggleSort} />
              <SortHeader label="Journal" k="linked_journal_no" sortBy={sortBy} sortDir={sortDir} onClick={toggleSort} />
              <SortHeader label="Created" k="created_at" sortBy={sortBy} sortDir={sortDir} onClick={toggleSort} />
              <th className="px-3 py-2 text-xs uppercase tracking-wider opacity-70">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan="11" className="px-3 py-12 text-center text-slate-500 italic">
                  No credit notes match your filters.
                </td>
              </tr>
            ) : filtered.map((cn) => (
              <React.Fragment key={cn.id}>
              <tr
                ref={registerRow(cn.id)}
                data-testid={`cn-row-${cn.id}`}
                className={`border-b border-slate-100 hover:bg-slate-50 cursor-pointer ${
                  expandedCnId === cn.id ? 'bg-violet-50' : ''
                } ${focusedId === cn.id ? 'focus-highlight' : ''}`}
                onClick={() => setExpandedCnId(expandedCnId === cn.id ? null : cn.id)}
              >
                <td className="px-3 py-2">
                  {cn.status === 'draft' && (
                    <button onClick={() => toggleSelect(cn.id)}
                      data-testid={`cn-select-${cn.id}`}>
                      {selected.has(cn.id)
                        ? <CheckSquare size={14} className="text-blue-600" />
                        : <Square size={14} className="text-slate-400" />}
                    </button>
                  )}
                </td>
                <td className="px-3 py-2 font-mono font-semibold">{cn.credit_note_number}</td>
                <td className="px-3 py-2 font-mono text-xs">
                  <a href={`/invoices?focus=${cn.invoice_id}`}
                    data-testid={`cn-open-invoice-${cn.id}`}
                    className="text-blue-600 hover:underline inline-flex items-center gap-1">
                    {cn.invoice_number || cn.invoice_id.slice(0, 8)} <Eye size={11} />
                  </a>
                </td>
                <td className="px-3 py-2">{TYPE_LABELS[cn.type] || cn.type}</td>
                <td className="px-3 py-2 text-right font-mono">{formatMoney(cn.amount)}</td>
                <td className="px-3 py-2 text-right font-mono text-slate-500">{formatMoney(cn.tax_amount)}</td>
                <td className="px-3 py-2 text-right font-mono font-semibold">{formatMoney(cn.total_amount)}</td>
                <td className="px-3 py-2">
                  <span className={`px-2 py-0.5 rounded text-xs uppercase font-semibold border ${STATUS_BADGE[cn.status] || ''}`}>
                    {cn.status}
                  </span>
                </td>
                <td className="px-3 py-2 font-mono text-xs">{cn.linked_journal_no || '—'}</td>
                <td className="px-3 py-2 text-xs text-slate-500">{formatDate(cn.created_at)}</td>
                <td className="px-3 py-2">
                  <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                    <button
                      data-testid={`cn-versions-${cn.id}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        setExpandedCnId(expandedCnId === cn.id ? null : cn.id);
                      }}
                      title="سجل الإصدارات"
                      className="p-1 rounded text-violet-600 hover:bg-violet-50"
                    >
                      <History size={14} />
                    </button>
                    {cn.status === 'posted' && isAdmin && (
                      <button onClick={() => reverseOne(cn)}
                        data-testid={`cn-reverse-${cn.id}`}
                        title="Reverse (admin)"
                        className="p-1 rounded text-rose-600 hover:bg-rose-50">
                        <RotateCcw size={14} />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
              {expandedCnId === cn.id && (
                <tr data-testid={`cn-expanded-${cn.id}`}>
                  <td colSpan="11" className="p-3 bg-slate-50">
                    <CreditNoteVersionsPanel
                      creditNote={cn}
                      onUpdated={() => fetchAll()}
                    />
                  </td>
                </tr>
              )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {/* ── Footer hint ───────────────────────────────────── */}
      <div className="text-xs text-slate-500 flex items-center gap-2">
        <AlertTriangle size={12} className="text-amber-500" />
        Credit Notes are created from the <a href="/invoices" className="text-blue-600 hover:underline">Invoices page</a> via the
        orange button next to a non-draft invoice. Reverse requires admin role.
      </div>
    </div>
  );
}
